import { useState, useEffect, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import Cookies from "js-cookie";
import httpClient from "../../../../../api/httpClient";
import notify from "../../../../../components/ACS-Dashboard/ACS-Blocks/Toast";
import {
  addTab as addInvoiceTab,
  setActiveTab as setActiveInvoiceTab,
} from "../../../../../redux/slices/invoiceSlice";

/**
 * Encapsulates all parent-invoice button logic for ShipmentDetailsView.
 * Returns canCreateParentInvoice, canViewParentInvoice, parentInvoiceNumber,
 * isCreatingParentInvoice, and handleCreateParentInvoice.
 */
function readBrokerNameFromStorage(officeCode) {
  try {
    const orgs = JSON.parse(localStorage.getItem("organizationData") || "[]");
    const match = orgs.find((o) => o.officeCode === officeCode);
    return match?.officeName || "";
  } catch {
    return "";
  }
}

export function useParentInvoice({ shipmentNumber, officeCode, isMultiLegParent, isShipper, shipmentLegs, isInvoiced }) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { defaultTabs, dynamicTabs } = useSelector((s) => s.invoices);

  const [exists, setExists] = useState(false);
  const [parentInvoiceNumber, setParentInvoiceNumber] = useState(null);
  const [existsLoading, setExistsLoading] = useState(true);
  const [isCreating, setIsCreating] = useState(false);
  // Hide the Create button immediately when the invoice is saved in NewCarrierInvoice.
  useEffect(() => {
    const handler = (e) => {
      if (e.detail?.parentShipmentNumber === shipmentNumber) {
        setExists(true);
        if (e.detail?.invoiceNumber) setParentInvoiceNumber(e.detail.invoiceNumber);
      }
    };
    window.addEventListener("parentInvoiceCreated", handler);
    return () => window.removeEventListener("parentInvoiceCreated", handler);
  }, [shipmentNumber]);

  const allLegsApproved =
    Array.isArray(shipmentLegs) &&
    shipmentLegs.length > 0 &&
    shipmentLegs.every((leg) => leg.isInvoiced && leg.invoiceStatus === "Approved");

  // Gate for showing ANY parent-invoice UI (role + shipment type).
  // Existence check is NOT gated on allLegsApproved so that View Invoice remains
  // visible even if a leg status changes after the invoice was created.
  const shouldCheck = isMultiLegParent && isShipper;
  const canCreate  = shouldCheck && allLegsApproved && !isInvoiced;

  useEffect(() => {
    if (!shouldCheck || !shipmentNumber || !officeCode) {
      setExistsLoading(false);
      return;
    }
    setExistsLoading(true);
    const token = Cookies.get("authToken");
    httpClient
      .get("/api/invoices/parent-invoice-exists", {
        params: { parentShipmentNumber: shipmentNumber },
        headers: { Authorization: `Bearer ${token}`, "Office-Code": officeCode },
      })
      .then((res) => {
        setExists(res.data?.exists === true);
        if (res.data?.invoiceNumber) setParentInvoiceNumber(res.data.invoiceNumber);
      })
      .catch(() => {})
      .finally(() => setExistsLoading(false));
  }, [shouldCheck, shipmentNumber, officeCode]);

  const handleCreateParentInvoice = useCallback(async () => {
    const token = Cookies.get("authToken");
    setIsCreating(true);
    try {
      const { data } = await httpClient.get(
        `/api/shipments/detailed-shipment/${shipmentNumber}`,
        { headers: { Authorization: `Bearer ${token}`, "Office-Code": officeCode } }
      );

      // Aggregate REVENUE charges by code so the invoice shows one row per charge type.
      const aggregatedMap = (data.charges || [])
        .filter(c => (c.type || "").toUpperCase() === "REVENUE")
        .reduce((acc, c) => {
          const key = c.code || "";
          if (!acc[key]) {
            acc[key] = { code: key, chargeName: c.chargeName || "", amount: 0, currency: c.currency || "USD", type: "REVENUE" };
          }
          acc[key].amount += Number(c.amount || 0);
          return acc;
        }, {});
      const aggregatedCharges = Object.values(aggregatedMap);
      const totalRevenue = aggregatedCharges.reduce((sum, c) => sum + c.amount, 0);

      const shipper  = data.shipperOfficeCode  || {};
      const receiver = data.receiverOfficeCode || {};

      const prefilled = [
        {
          shipmentNumber: data.shipmentNumber,
          shipmentOfficeCode: officeCode,
          invoiceType: "PARENT",
          parentShipmentNumber: shipmentNumber,

          // billTo: the shipper (customer who pays the broker)
          billToOfficeCode:   shipper.partyOfficeCode || "",
          billToCompanyName:  shipper.name            || "",
          billToAddress:      shipper.addressLine1    || "",
          billToCity:         shipper.city            || "",
          billToState:        shipper.state           || "",
          billToZip:          shipper.zipCode         || "",
          billToCountry:      shipper.country         || "",
          billToPhone:        shipper.phone           || "",
          billToEmail:        shipper.email           || "",
          billToFax:          shipper.contactFax      || "",

          // payTo: the broker (receives payment from customer)
          payToOfficeCode:    officeCode,
          payToCompanyName:   readBrokerNameFromStorage(officeCode),
          payToAddress:       "",
          payToCity:          "",
          payToState:         "",
          payToZip:           "",
          payToCountry:       "",
          payToPhone:         "",
          payToEmail:         "",
          payToFax:           "",

          // shipTo: the receiver (delivery destination)
          shipToCompanyName:  receiver.name         || "",
          shipToAddress:      receiver.addressLine1 || "",
          shipToCity:         receiver.city         || "",
          shipToState:        receiver.state        || "",
          shipToZip:          receiver.zipCode      || "",
          shipToCountry:      receiver.country      || "",
          shipToPhone:        receiver.phone        || "",
          shipToEmail:        receiver.email        || "",
          shipToFax:          receiver.contactFax   || "",

          shipmentCharge: totalRevenue.toFixed(2),
          charges: aggregatedCharges,
        },
      ];

      dispatch(
        addInvoiceTab({
          id: `invoice-new-${Date.now()}`,
          name: "Parent Invoice",
          type: "new",
          data: prefilled,
        })
      );
      dispatch(
        setActiveInvoiceTab({
          index: defaultTabs.length + dynamicTabs.length,
        })
      );
      navigate("/dashboard/invoices");
    } catch (err) {
      const msg = err?.response?.data?.content || "Failed to load shipment details.";
      notify.error({ title: "Error", message: msg });
    } finally {
      setIsCreating(false);
    }
  }, [shipmentNumber, officeCode, dispatch, navigate, defaultTabs.length, dynamicTabs.length]);

  return {
    // Suppress Create while the existence check is in-flight so there is no flash.
    canCreateParentInvoice: !existsLoading && canCreate && !exists,
    canViewParentInvoice:   !existsLoading && shouldCheck && exists,
    parentInvoiceNumber,
    isCreatingParentInvoice: isCreating,
    handleCreateParentInvoice,
  };
}
